import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface SpookyParticle {
  id: number;
  x: number;
  y: number;
  delay: number;
  size: number;
  type: "ghost" | "bat";
  color: string;
}

export const HalloweenSpooky = () => {
  const [particles, setParticles] = useState<SpookyParticle[]>([]);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    // Generate spooky particles
    const particleCount = 4 + Math.floor(Math.random() * 3); // 4-6 particles
    const newParticles: SpookyParticle[] = [];

    for (let i = 0; i < particleCount; i++) {
      const isGhost = Math.random() > 0.5;
      newParticles.push({
        id: i,
        x: Math.random() * 100, // 0-100%
        y: Math.random() * 100, // 0-100%
        delay: Math.random() * 3, // 0-3s delay
        size: 12 + Math.random() * 8, // 12-20px
        type: isGhost ? "ghost" : "bat",
        color: isGhost ? "rgba(255,255,255,0.3)" : "#FF6B35",
      });
    }

    setParticles(newParticles);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Don't render if user prefers reduced motion
  if (prefersReducedMotion) {
    return null;
  }

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      aria-hidden="true"
    >
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            willChange: "transform, opacity",
          }}
          animate={{
            y: [0, -20, -10, 0], // Slow eerie floating
            x: [0, 15, -10, 0], // Eerie sway
            opacity: [0.3, 0.7, 0.5, 0.3], // Ghostly fade in/out
            rotate: particle.type === "bat" ? [0, -10, 10, 0] : [0, 5, -5, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
            delay: particle.delay,
          }}
        >
          {particle.type === "ghost" ? (
            // Ghost SVG
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
            >
              <path
                d="M12 3C8.13 3 5 6.13 5 10V19L7 17L9 19L11 17L13 19L15 17L17 19L19 17V10C19 6.13 15.87 3 12 3Z"
                fill={particle.color}
              />
              <circle cx="9" cy="10" r="1.5" fill="rgba(0,0,0,0.5)" />
              <circle cx="15" cy="10" r="1.5" fill="rgba(0,0,0,0.5)" />
            </svg>
          ) : (
            // Bat SVG
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
            >
              <path
                d="M12 2C10 2 8 4 8 6C6 6 4 8 4 10C4 11 5 12 6 12L8 14L10 12C10 10 11 8 12 8C13 8 14 10 14 12L16 14L18 12C19 12 20 11 20 10C20 8 18 6 16 6C16 4 14 2 12 2Z"
                fill={particle.color}
              />
              <circle cx="12" cy="8" r="2" fill={particle.color} />
            </svg>
          )}
        </motion.div>
      ))}
    </div>
  );
};
